import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import { Link } from "react-router-dom"
import {
  Activity,
  AlertTriangle,
  ChevronRight,
  UserMinus,
  Loader2,
  PhoneOutgoing,
  ShieldCheck,
  Sparkles,
} from "lucide-react"
import { APCMState } from "../../types/apcm-ops.types"
import * as api from "../../api/apcm-ops.api"

interface Props {
  orgId: string
  stateCounts: Record<string, number>
  /** Calls with an enabled schedule from now on; null while loading. */
  scheduledCallCount: number | null
  onViewPatients: (states: string[]) => void
  onOpenSetup: () => void
  onRefresh?: () => void
}

interface GenStatus {
  is_active: boolean
  generating: number
  completed: number
  failed: number
  sqs: { messages_available: number; messages_in_flight: number }
}

export function ActivityRail({
  orgId,
  stateCounts,
  scheduledCallCount,
  onViewPatients,
  onOpenSetup,
}: Props) {
  const [genStatus, setGenStatus] = useState<GenStatus | null>(null)

  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    const load = async () => {
      try {
        const s = await api.getCarePlanGenerationStatus(orgId)
        if (!cancelled) setGenStatus(s)
      } catch {
        /* ignore */
      }
    }
    load()
    const id = setInterval(load, 20000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [orgId])

  const disenrolled = stateCounts[APCMState.DISENROLLED] || 0
  const schedulingFailed = stateCounts[APCMState.SCHEDULING_FAILED] || 0
  const carePlanGenerating = stateCounts[APCMState.CARE_PLAN_GENERATING] || 0
  const carePlanUploading = stateCounts[APCMState.CARE_PLAN_UPLOADING] || 0
  const consentScheduled = stateCounts[APCMState.CONSENT_SCHEDULED] || 0
  const checkInScheduled = stateCounts[APCMState.CHECK_IN_SCHEDULED] || 0

  const hasActivity =
    carePlanGenerating > 0 ||
    carePlanUploading > 0 ||
    consentScheduled > 0 ||
    checkInScheduled > 0 ||
    (genStatus?.sqs?.messages_in_flight ?? 0) > 0

  return (
    <div className="space-y-4">
      {/* Scheduled calls: what the scheduler will dial, regardless of pipeline state */}
      <section>
        <div className="flex items-center gap-2 mb-2.5">
          <PhoneOutgoing className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Scheduled calls
          </h3>
        </div>
        <Link
          to="/upcoming-calls"
          className="block rounded-xl border bg-card px-3 py-2.5 hover:bg-accent/40 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="text-base font-bold tabular-nums">
              {scheduledCallCount === null ? "–" : scheduledCallCount}
            </span>
            <span className="text-xs text-muted-foreground">
              {scheduledCallCount === 1 ? "call queued to dial" : "calls queued to dial"}
            </span>
            <ChevronRight className="h-3 w-3 text-muted-foreground ml-auto" />
          </div>
          <p className="mt-1 text-[10px] text-muted-foreground">
            Dialled by Retell when each schedule fires. Check the Setup tab for dial-out readiness.
          </p>
        </Link>
      </section>

      {/* Now running */}
      <section>
        <div className="flex items-center gap-2 mb-2.5">
          <Activity className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Now Running
          </h3>
        </div>
        {hasActivity ? (
          <div className="rounded-xl border bg-card divide-y">
            {consentScheduled > 0 && (
              <ActivityRow count={consentScheduled} label="consent calls in flight" />
            )}
            {carePlanGenerating > 0 && (
              <ActivityRow count={carePlanGenerating} label="care plans generating" pulse />
            )}
            {carePlanUploading > 0 && (
              <ActivityRow count={carePlanUploading} label="care plans uploading to ECW" pulse />
            )}
            {checkInScheduled > 0 && (
              <ActivityRow count={checkInScheduled} label="check-in calls in flight" />
            )}
            {genStatus && genStatus.sqs && genStatus.sqs.messages_in_flight > 0 && (
              <ActivityRow
                count={genStatus.sqs.messages_in_flight}
                label="SQS messages in flight"
                sub={`${genStatus.sqs.messages_available} waiting`}
              />
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed bg-muted/10 px-3 py-4 text-center">
            <p className="text-[11px] text-muted-foreground">No active workflows</p>
          </div>
        )}
      </section>

      {/* Needs attention */}
      {schedulingFailed > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2.5">
            <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
            <h3 className="text-[11px] font-semibold uppercase tracking-widest text-destructive">
              Needs Attention
            </h3>
          </div>
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 space-y-2">
            <button
              onClick={() => onViewPatients([APCMState.SCHEDULING_FAILED])}
              className="w-full text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-base font-bold text-destructive tabular-nums">
                  {schedulingFailed}
                </span>
                <span className="text-xs text-destructive">scheduling failed</span>
                <ChevronRight className="h-3 w-3 text-destructive ml-auto" />
              </div>
            </button>
            <p className="text-[11px] leading-relaxed text-destructive/90">
              The scheduler could not place a call for these patients. Open the patient drawer
              to see the blocker, then move them back to the needed state to retry.
            </p>
          </div>
        </section>
      )}

      {/* Off pipeline */}
      {disenrolled > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2.5">
            <UserMinus className="h-3.5 w-3.5 text-muted-foreground" />
            <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Off Pipeline
            </h3>
          </div>
          <button
            onClick={() => onViewPatients([APCMState.DISENROLLED])}
            className="w-full rounded-xl border bg-card px-3 py-2.5 text-left hover:bg-accent/40 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-base font-bold tabular-nums">{disenrolled}</span>
              <span className="text-xs text-muted-foreground">disenrolled</span>
              <ChevronRight className="h-3 w-3 text-muted-foreground ml-auto" />
            </div>
          </button>
        </section>
      )}

      {/* Setup / shortcuts */}
      <section>
        <div className="flex items-center gap-2 mb-2.5">
          <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Shortcuts
          </h3>
        </div>
        <div className="rounded-xl border bg-card divide-y">
          <ShortcutLink icon={ShieldCheck} label="Setup & reconciliation" onClick={onOpenSetup} />
          <ShortcutLink
            icon={Activity}
            label="Billing codes"
            href="/billing-codes"
            external={false}
          />
        </div>
      </section>
    </div>
  )
}

function ActivityRow({
  count,
  label,
  sub,
  pulse,
}: {
  count: number
  label: string
  sub?: string
  pulse?: boolean
}) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2.5">
      <span
        className={cn(
          "h-2 w-2 rounded-full shrink-0 bg-info",
          pulse && "animate-pulse",
        )}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          <span className="text-sm font-semibold tabular-nums">{count}</span>
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      <Loader2 className="h-3 w-3 text-muted-foreground animate-spin" />
    </div>
  )
}

function ShortcutLink({
  icon: Icon,
  label,
  onClick,
  href,
  external,
}: {
  icon: React.ElementType
  label: string
  onClick?: () => void
  href?: string
  external?: boolean
}) {
  const inner = (
    <div className="flex items-center gap-2.5 px-3 py-2.5 w-full">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs flex-1 text-left">{label}</span>
      <ChevronRight className="h-3 w-3 text-muted-foreground" />
    </div>
  )
  if (href) {
    return (
      <Link
        to={href}
        target={external ? "_blank" : undefined}
        className="block hover:bg-accent/40 transition-colors"
      >
        {inner}
      </Link>
    )
  }
  return (
    <button type="button" onClick={onClick} className="w-full hover:bg-accent/40 transition-colors">
      {inner}
    </button>
  )
}
